/* global window */
import React from 'react';

// Single source of truth for the mobile cutover. Matches antd's `lg` so page
// level <Col lg={...}> props line up with the shell's behaviour.
export const MOBILE_BREAKPOINT = 992;

const getWidth = () =>
  typeof window === 'undefined' ? MOBILE_BREAKPOINT : window.innerWidth;

// For function components.
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(
    () => getWidth() < MOBILE_BREAKPOINT
  );

  React.useEffect(() => {
    let frame = null;

    const onResize = () => {
      // Resize fires continuously while an orientation change settles, so
      // coalesce into one state update per frame.
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setIsMobile(getWidth() < MOBILE_BREAKPOINT);
      });
    };

    window.addEventListener('resize', onResize);
    // The first paint can happen before layout settles on mobile browsers.
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return isMobile;
};

// For class components, which can't call the hook directly.
export const withIsMobile = Component => {
  const Wrapped = props => {
    const isMobile = useIsMobile();
    return <Component {...props} isMobile={isMobile} />;
  };

  Wrapped.displayName = `withIsMobile(${Component.displayName ||
    Component.name ||
    'Component'})`;

  return Wrapped;
};
